import { DASHBOARD_REGIONS, getDashboardRegion, getRegionFromURL, setRegionInURL } from '../data/regions.js';

export function createRegionSelector() {
  // Read the starting region from the URL (?region=asia) up front, so the
  // very first render — not just the post-click state — already reflects
  // a shared link. Every other component reacts to the same initial
  // ess:region-change dispatch below, so this one read is enough to make
  // the whole dashboard start on the right region.
  const initialRegionKey = getRegionFromURL();

  const section = document.createElement('section');
  section.id = 'dashboard-region-selector';
  section.className = 'section section--region-selector';
  section.innerHTML = `
    <div class="container">
      <div class="dashboard-region glass-card anim-on-scroll">
        <div>
          <div class="dashboard-region__label">Dashboard Geography</div>
          <div class="dashboard-region__title" id="dashboardRegionTitle">${getDashboardRegion(initialRegionKey).label}</div>
        </div>
        <div class="dashboard-region__control" role="radiogroup" aria-label="Dashboard geography">
          ${DASHBOARD_REGIONS.map(region => `
            <button class="dashboard-region-option ${region.key === initialRegionKey ? 'active' : ''}"
              type="button"
              role="radio"
              aria-checked="${region.key === initialRegionKey ? 'true' : 'false'}"
              data-region="${region.key}">
              <span class="dashboard-region-option__icon">${region.icon}</span>
              <span class="dashboard-region-option__body">
                <span class="dashboard-region-option__label">${region.label}</span>
                <span class="dashboard-region-option__hint">${region.hint}</span>
              </span>
            </button>
          `).join('')}
        </div>
      </div>
    </div>
  `;

  injectRegionSelectorStyles();
  requestAnimationFrame(() => {
    const title = section.querySelector('#dashboardRegionTitle');
    const buttons = Array.from(section.querySelectorAll('.dashboard-region-option'));

    function setRegion(regionKey, { updateUrl = true } = {}) {
      const region = getDashboardRegion(regionKey);
      title.textContent = region.label;
      buttons.forEach(button => {
        const isActive = button.dataset.region === region.key;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
      if (updateUrl) setRegionInURL(region.key);
      // isInitial distinguishes the mount-time sync call below (updateUrl:
      // false) from a real user click, so listeners that only care about
      // deliberate region switches (e.g. visit tracking) can ignore the former.
      window.dispatchEvent(new CustomEvent('ess:region-change', { detail: { region, isInitial: !updateUrl } }));
    }

    buttons.forEach(button => {
      button.addEventListener('click', () => setRegion(button.dataset.region));
    });

    // Initial dispatch only — don't rewrite a URL the visitor already typed
    // or clicked their way to.
    setRegion(initialRegionKey, { updateUrl: false });
  });

  return section;
}

function injectRegionSelectorStyles() {
  if (document.getElementById('region-selector-styles')) return;
  const style = document.createElement('style');
  style.id = 'region-selector-styles';
  style.textContent = `
    .section--region-selector {
      padding: var(--space-8) 0 var(--space-4);
      background: var(--clr-deep);
    }

    .dashboard-region {
      display: grid;
      grid-template-columns: minmax(180px, 0.28fr) 1fr;
      align-items: stretch;
      gap: var(--space-5);
      padding: var(--space-5);
      border-radius: var(--radius-lg);
    }

    .dashboard-region__label {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .dashboard-region__title {
      margin-top: 2px;
      font-family: var(--font-display);
      font-size: var(--text-2xl);
      font-weight: var(--weight-bold);
      color: var(--clr-text-primary);
    }

    .dashboard-region__control {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: var(--space-2);
    }

    .dashboard-region-option {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      gap: var(--space-3);
      min-height: 72px;
      padding: var(--space-3);
      border-radius: var(--radius-md);
      border: 1px solid rgba(255,255,255,0.1);
      background: var(--clr-surface);
      color: var(--clr-text-primary);
      text-align: left;
      transition: border-color var(--dur-normal) var(--ease-out),
                  background var(--dur-normal) var(--ease-out),
                  box-shadow var(--dur-normal) var(--ease-out),
                  transform var(--dur-fast) var(--ease-out);
    }

    .dashboard-region-option:hover {
      transform: translateY(-1px);
      border-color: rgba(141,197,61,0.3);
      background: var(--clr-surface-2);
    }

    .dashboard-region-option.active {
      border-color: rgba(141,197,61,0.75);
      background: linear-gradient(135deg, rgba(141,197,61,0.18), rgba(18,27,53,0.9));
      box-shadow: 0 0 0 1px rgba(141,197,61,0.15), 0 12px 28px rgba(0,0,0,0.22);
    }

    .dashboard-region-option__icon {
      display: grid;
      place-items: center;
      width: 38px;
      height: 38px;
      border-radius: var(--radius-md);
      background: rgba(255,255,255,0.06);
      font-size: 20px;
    }

    .dashboard-region-option.active .dashboard-region-option__icon {
      background: rgba(141,197,61,0.16);
    }

    .dashboard-region-option__body {
      display: grid;
      gap: 2px;
      min-width: 0;
    }

    .dashboard-region-option__label {
      font-size: var(--text-sm);
      font-weight: var(--weight-semi);
      color: var(--clr-text-primary);
      overflow-wrap: anywhere;
    }

    .dashboard-region-option__hint {
      font-size: 11px;
      color: var(--clr-text-tertiary);
      line-height: 1.25;
    }

    @media (max-width: 980px) {
      .dashboard-region {
        grid-template-columns: 1fr;
      }
      .dashboard-region__control {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 560px) {
      .dashboard-region {
        padding: var(--space-4);
      }

      .dashboard-region__control {
        grid-template-columns: 1fr;
      }

      .dashboard-region-option {
        min-height: 60px;
      }
    }
  `;
  document.head.appendChild(style);
}
